import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Bearer-token fallback for deployments where cookies can't cross the topology
// (custom domain → emergent.host backend etc.). The login endpoint echoes the access
// token in its response body; we cache it in localStorage and attach it as a header.
const TOKEN_KEY = "kitt_access_token";
export function storeBearerToken(token) {
    try {
        if (token) window.localStorage.setItem(TOKEN_KEY, token);
    } catch {
        /* ignore */
    }
}
export function clearBearerToken() {
    try {
        window.localStorage.removeItem(TOKEN_KEY);
    } catch {
        /* ignore */
    }
}
function getBearerToken() {
    try {
        return window.localStorage.getItem(TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

const api = axios.create({
    baseURL: API,
    withCredentials: true,
});

// Inject Authorization header on every outgoing request if we have a token.
api.interceptors.request.use((config) => {
    const tok = getBearerToken();
    if (tok) {
        config.headers = config.headers || {};
        if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${tok}`;
    }
    return config;
});

// Global 401 handler — bounce to /login at most once per page load.
// IMPORTANT: A 'kitt_session' marker (cookie OR localStorage) means a live session
// exists; in that case we DO NOT redirect on background-fetch 401s (e.g., audit log race).
// We only redirect when the marker is missing AND we're not already on /login.
let _redirectingTo401 = false;

export function resetAuthRedirectFlag() {
    _redirectingTo401 = false;
}

function hasSessionMarker() {
    if (typeof window === "undefined") return false;
    try {
        if (window.localStorage && window.localStorage.getItem("kitt_session") === "1") return true;
        // Legacy compatibility for any user mid-session during the rebrand.
        if (window.localStorage && window.localStorage.getItem("cerberus_session") === "1") return true;
    } catch {
        /* ignore */
    }
    if (typeof document !== "undefined" && document.cookie) {
        if (/(?:^|;\s*)kitt_session=1/.test(document.cookie)) return true;
        if (/(?:^|;\s*)cerberus_session=1/.test(document.cookie)) return true;
    }
    return false;
}

api.interceptors.response.use(
    (resp) => resp,
    (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const onAuthRoute =
            url.includes("/auth/me") ||
            url.includes("/auth/login") ||
            url.includes("/auth/register") ||
            url.includes("/auth/logout");
        const alreadyOnLogin =
            typeof window !== "undefined" && window.location.pathname === "/login";

        if (status === 401 && !onAuthRoute && !alreadyOnLogin) {
            if (hasSessionMarker()) {
                // Live session per marker — likely a transient race; do NOT redirect.
                // The page-level try/catch handlers will swallow the rejection so React
                // never shows its dev runtime-error overlay.
                console.warn("[AUTH] 401 suppressed (session marker present):", url);
            } else if (!_redirectingTo401) {
                _redirectingTo401 = true;
                // A stale Bearer token will cause an infinite 401 loop; clear it.
                clearBearerToken();
                console.warn("[AUTH] Redirecting to /login: no session marker. URL:", url);
                window.location.replace("/login");
            }
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
